/**
 * PremierWorkspace — read-only partner lookup against the Premier economy
 * system (ApiComPrem over the office WireGuard tunnel). First integration
 * surface: proves connectivity + auth end-to-end and lets admins find a
 * partner (name fragment or exact IČO) without opening Premier itself.
 * Spravce-gated server-side (`/v1/premier/*`).
 */

import { useState } from 'react';
import { Badge } from '@/components/Badge';
import { EmptyState } from '@/components/EmptyState';
import { SearchInput } from '@/components/SearchInput';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { ApiError } from '@/lib/api';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { usePremierPartneriQuery, usePremierStatusQuery } from './api';

interface PremierWorkspaceProps {
  canManage: boolean;
}

export function PremierWorkspace({ canManage }: PremierWorkspaceProps) {
  const [search, setSearch] = useState('');
  const term = useDebouncedValue(search, 350);

  const status = usePremierStatusQuery(canManage);
  const query = usePremierPartneriQuery(term, canManage && status.data?.connected === true);

  if (!canManage) {
    return (
      <div className="flex min-h-0 flex-1 flex-col px-8 pb-8 pt-5">
        <EmptyState
          eyebrow="403"
          title="Pouze pro správce"
          description="Premier data vidí jen správci firmy a administrátoři."
        />
      </div>
    );
  }

  const partneri = query.data?.partneri ?? [];
  const searching = term.trim().length >= 2;
  const errorMessage =
    query.isError && query.error instanceof ApiError ? query.error.message : null;

  return (
    <div data-testid="premier-workspace" className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <SearchInput
          wrapperClassName="min-w-[320px]"
          kbd={null}
          placeholder="Hledat partnera — název nebo IČO…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          disabled={status.data?.connected !== true}
        />
        <div className="ml-auto flex items-center gap-2">
          {status.isLoading ? (
            <Skeleton className="h-5 w-28" />
          ) : !status.data?.configured ? (
            <Badge tone="neutral">Nenakonfigurováno</Badge>
          ) : status.data.connected ? (
            <Badge tone="ok">Premier připojen</Badge>
          ) : (
            <Badge tone="danger">Premier nedostupný</Badge>
          )}
        </div>
      </Toolband>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">

      {!status.isLoading && !status.data?.configured ? (
        <EmptyState
          title="Premier není nakonfigurován"
          description="Pro tuto firmu chybí připojení k systému Premier. Konfiguraci provádí administrátor intranetu."
        />
      ) : !status.isLoading && status.data?.connected === false ? (
        <EmptyState
          eyebrow="Chyba"
          title="Premier server neodpovídá"
          description={status.data.error ?? 'Zkontrolujte tunel a službu ApiComPrem.'}
        />
      ) : !searching ? (
        <EmptyState
          title="Vyhledejte partnera"
          description="Zadejte alespoň 2 znaky názvu, nebo celé IČO. Data se čtou přímo z Premieru."
        />
      ) : query.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : errorMessage ? (
        <EmptyState eyebrow="Chyba" title="Hledání selhalo" description={errorMessage} />
      ) : partneri.length === 0 ? (
        <EmptyState
          title="Žádný partner nenalezen"
          description="Zkuste jiný fragment názvu nebo přesné IČO."
        />
      ) : (
        <>
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3">
            Nalezeno {query.data?.total}
            {(query.data?.total ?? 0) > partneri.length
              ? ` · zobrazeno prvních ${partneri.length}`
              : ''}
          </span>
          <div className="overflow-x-auto border border-line bg-paper">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-line bg-surface-2 text-left font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3">
                  <th className="px-3 py-2 font-medium">Název</th>
                  <th className="px-3 py-2 font-medium">IČO</th>
                  <th className="px-3 py-2 font-medium">DIČ</th>
                  <th className="px-3 py-2 font-medium">Adresa</th>
                  <th className="px-3 py-2 font-medium">Kontakt</th>
                  <th className="px-3 py-2 font-medium">Vztah</th>
                </tr>
              </thead>
              <tbody>
                {partneri.map((p) => (
                  <tr key={p.inter} className="border-b border-line align-top last:border-0">
                    <td className="px-3 py-2.5">
                      <span className="font-medium text-ink">{p.nazev}</span>
                      {p.cislo ? (
                        <span className="ml-2 font-mono text-[11px] text-ink-3">{p.cislo}</span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[11.5px] text-ink-2">
                      {p.ico || '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[11.5px] text-ink-2">
                      {p.dic || '—'}
                    </td>
                    <td className="px-3 py-2.5 text-ink-2">
                      {[p.ulice, [p.psc, p.mesto].filter(Boolean).join(' ')]
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </td>
                    <td className="px-3 py-2.5 text-ink-2">
                      {[p.email, p.tel || p.mobil].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {p.odberatel ? <Badge tone="accent">Odběratel</Badge> : null}
                      {p.dodavatel ? (
                        <Badge tone="neutral" className={p.odberatel ? 'ml-1' : ''}>
                          Dodavatel
                        </Badge>
                      ) : null}
                      {!p.odberatel && !p.dodavatel ? '—' : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
